import React, { useState } from 'react';
import { Drawer, SIZE, ANCHOR } from 'baseui/drawer';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Select } from 'baseui/select';
import { Button, KIND, SIZE as BUTTON_SIZE } from 'baseui/button';
import { HeadingMedium } from 'baseui/typography';
import { styled } from 'styletron-react';
import { toaster } from 'baseui/toast';
import { useCreateUser } from '../hooks/useSAMUsers';
import { useRoles } from '../hooks/useRoles';
import { useBusinessContext } from '../context/BusinessContext';
import { useTenantIds } from '../hooks/useTenant';
import { useEstatesWithGroups } from '../hooks/useEstates';
import { defaultInstance } from '../api/httpClient';
import { findDbUserIdForCreatedUser } from '../api/dbUserLookup';
import { businessRequiredMessage } from '../utils/apiErrors';
import { groupNeedsBusiness, hasBusinessId } from '../utils/userAdmin';

const DrawerContainer = styled('div', {
  padding: '24px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const FormSection = styled('div', {
  flex: 1,
  overflowY: 'auto',
  marginBottom: '24px',
});

const Footer = styled('div', {
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
  paddingTop: '16px',
  borderTop: '1px solid #E2E8F0',
});

interface CreateUserDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  defaultGroupId?: string;
}

export const CreateUserDrawer: React.FC<CreateUserDrawerProps> = ({ isOpen, onClose, defaultGroupId }) => {
  const { createUserAsync, isCreating } = useCreateUser();
  const { businessGroups } = useRoles();
  const { currentBusinessId } = useBusinessContext();
  const { currentTenantId } = useTenantIds();
  const { data: estatesData } = useEstatesWithGroups();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    family_name: '',
    phone_number: '',
  });
  const [selectedGroup, setSelectedGroup] = useState<Array<{ id: string; label: string }>>([]);
  const [selectedEstate, setSelectedEstate] = useState<Array<{ id: string; label: string }>>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isPropertyManager = selectedGroup.length > 0 && selectedGroup[0].id === 'business_property_manager';

  const estateOptions = React.useMemo(() => {
    const estates = (estatesData as any)?.data || estatesData || [];
    if (!Array.isArray(estates)) return [];
    return estates.map((e: any) => ({
      id: String(e.id),
      label: `${e.name}${e.estate_group?.name ? ` — ${e.estate_group.name}` : ''}`,
    }));
  }, [estatesData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.name) {
      newErrors.name = 'Name is required';
    }

    if (!formData.family_name) {
      newErrors.family_name = 'Family name is required';
    }

    // A business role without a business would leave the account locked out (the server refuses it too).
    if (selectedGroup.length > 0 && groupNeedsBusiness(selectedGroup[0].id) && !hasBusinessId(currentBusinessId)) {
      newErrors.group = businessRequiredMessage();
    }

    if (isPropertyManager && selectedEstate.length === 0) {
      newErrors.estate = 'Estate is required for property managers';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const userData: any = {
      ...formData,
      group: selectedGroup.length > 0 ? selectedGroup[0].id : undefined,
      tenant_id: currentTenantId || undefined,
      custom_attributes: {
        tenant_id: currentTenantId || undefined,
      },
    };

    if (isPropertyManager) {
      if (selectedEstate.length > 0) {
        userData.custom_attributes.mainEstate = selectedEstate[0].id;
        userData.main_estate = selectedEstate[0].id;
      }
    } else {
      const businessId = hasBusinessId(currentBusinessId) ? currentBusinessId : undefined;
      userData.business_id = businessId;
      userData.custom_attributes.business_id = businessId;
    }

    try {
      const result = await createUserAsync(userData);

      // Close the drawer immediately — estate linking runs in the background
      handleClose();

      if (isPropertyManager && selectedEstate.length > 0) {
        const cognitoUsername = result.user.username;
        const estateId = Number(selectedEstate[0].id);
        linkEstateInBackground(cognitoUsername, userData.email, estateId, result.user_id);
      }
    } catch {
      // createUserAsync already shows the error toast via the hook's onError
    }
  };

  // Runs after the drawer closes — no loading state needed
  const linkEstateInBackground = async (cognitoUsername: string, email: string, estateId: number, knownUserId?: number | null) => {
    // cognitoid in the DB is the Cognito sub, not the username — resolve it first (retries while the row syncs)
    const dbUserId = await findDbUserIdForCreatedUser(cognitoUsername, email, { knownUserId });

    if (!dbUserId) {
      toaster.warning(
        'User created — assign the estate manually by opening the user from the list.',
        { autoHideDuration: 8000 }
      );
      return;
    }

    try {
      await defaultInstance({
        method: 'POST',
        url: '/user_estate',
        data: { user_id: dbUserId, estate_id: estateId, user_type_id: 5, status_id: 1 },
        timeout: 10000,
      });
      toaster.positive('User created and linked to estate successfully', { autoHideDuration: 4000 });
    } catch {
      toaster.warning(
        'User created — estate link failed. Open the user from the list to assign the estate.',
        { autoHideDuration: 8000 }
      );
    }
  };

  const handleClose = () => {
    if (isCreating) return;
    setFormData({ email: '', password: '', name: '', family_name: '', phone_number: '' });
    setSelectedGroup([]);
    setSelectedEstate([]);
    setErrors({});
    onClose();
  };

  const groupOptions = businessGroups
    .filter((g) => g.name.startsWith('business'))
    .map((g) => ({ id: g.name, label: g.name.replace('business_', '') }));

  // Pre-select default group if provided
  React.useEffect(() => {
    if (isOpen && selectedGroup.length === 0 && groupOptions.length > 0 && defaultGroupId) {
      const defaultOption = groupOptions.find(g => g.id === defaultGroupId);
      if (defaultOption) {
        setSelectedGroup([defaultOption]);
      }
    }
  }, [isOpen, groupOptions.length, defaultGroupId]);

  const popoverOverrides = {
    Popover: { props: { overrides: { Body: { style: { zIndex: 10001 } } } } },
  };

  return (
    <Drawer
      onClose={handleClose}
      isOpen={isOpen}
      anchor={ANCHOR.right}
      size={SIZE.default}
      overrides={{
        Root: { style: { zIndex: 9999 } },
        DrawerContainer: { style: { width: '500px', maxWidth: '100vw', zIndex: 10000 } },
      }}
    >
      <DrawerContainer>
        <HeadingMedium margin="0 0 24px 0">Create New Admin User</HeadingMedium>
        <FormSection>
          <FormControl label="Email *" error={errors.email}>
            <Input
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.currentTarget.value })}
              placeholder="user@example.com"
              error={!!errors.email}
              disabled={isCreating}
            />
          </FormControl>

          <FormControl label="Password *" error={errors.password} caption="Minimum 8 characters">
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.currentTarget.value })}
              placeholder="••••••••"
              error={!!errors.password}
              disabled={isCreating}
            />
          </FormControl>

          <FormControl label="First Name *" error={errors.name}>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.currentTarget.value })}
              placeholder="John"
              error={!!errors.name}
              disabled={isCreating}
            />
          </FormControl>

          <FormControl label="Last Name *" error={errors.family_name}>
            <Input
              value={formData.family_name}
              onChange={(e) => setFormData({ ...formData, family_name: e.currentTarget.value })}
              placeholder="Doe"
              error={!!errors.family_name}
              disabled={isCreating}
            />
          </FormControl>

          <FormControl label="Phone Number (optional)">
            <Input
              value={formData.phone_number}
              onChange={(e) => setFormData({ ...formData, phone_number: e.currentTarget.value })}
              placeholder="+1234567890"
              disabled={isCreating}
            />
          </FormControl>

          <FormControl label="Initial Group (optional)" error={errors.group}>
            <Select
              options={groupOptions}
              value={selectedGroup}
              onChange={(params) => {
                setSelectedGroup(params.value as any);
                // Clear estate if group changes away from property_manager
                if (!(params.value as any)?.[0]?.id?.includes('property_manager')) {
                  setSelectedEstate([]);
                }
              }}
              placeholder="Select a group..."
              clearable
              disabled={isCreating}
              overrides={popoverOverrides}
            />
          </FormControl>

          {isPropertyManager && (
            <FormControl label="Assigned Estate *" error={errors.estate} caption="The estate this property manager will manage">
              <Select
                options={estateOptions}
                value={selectedEstate}
                onChange={(params) => setSelectedEstate(params.value as any)}
                placeholder="Search and select estate..."
                clearable
                disabled={isCreating}
                overrides={popoverOverrides}
              />
            </FormControl>
          )}
        </FormSection>
        <Footer>
          <Button kind={KIND.tertiary} size={BUTTON_SIZE.default} onClick={handleClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            size={BUTTON_SIZE.default}
            onClick={handleSubmit}
            isLoading={isCreating}
            overrides={{
              BaseButton: {
                style: {
                  backgroundColor: 'var(--tenant-primary, #111827)',
                  ':hover': { backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' },
                },
              },
            }}
          >
            Create User
          </Button>
        </Footer>
      </DrawerContainer>
    </Drawer>
  );
};
